var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { CXoneAcdClient } from '@nice-devone/acd-sdk';
import { CXoneClient } from '@nice-devone/agent-sdk';
import { CXoneAuth } from '@nice-devone/auth-sdk';
import { CXoneVoiceClient, CXoneVoiceCallStatus } from '@nice-devone/voice-sdk';
import { AgentSessionStatus } from '@nice-devone/common-sdk';
import { ACDSessionManager, HttpClient, HttpUtilService, LocalStorageHelper, StorageKeys } from '@nice-devone/core-sdk';
import { message } from 'antd';
import logger from 'utils/logger';
import { useNicStore } from '../stores/nicStore';
import { useCallStore } from '../stores/callStore';
import { useContactStore } from '../stores/contactStore';
import { AGENT_PARTY_TYPE, useChatStore } from '../stores/chatStore';
import { MESSAGE_SOUND_LOCK_KEY, mergeByField } from '../utils/helpers';
// Raw get-next events the SDK has no parser for, so they never reach any of the
// typed contact subjects
var TEXT_CONTACT_EVENT = 'TextContactEvent';
var TEXT_MESSAGE_EVENT = 'TextMessage';
var INCOMING_STATUS = 'Incoming';
var DISCONNECTED_STATUS = 'Disconnected';
var AVAILABLE_STATE = 'Available';
// The SDK has no chat helpers, so these are called directly
var CHAT_TRANSCRIPT_URI = '/InContactAPI/services/v15.0/contacts/{contactId}/chat-transcript';
var SEND_CHAT_TEXT_URI = '/InContactAPI/services/v15.0/agent-sessions/{sessionId}/interactions/{contactId}/send-chat-text';
var showErrorAndLog = function (userMessage, logMessage, error) {
    message.error(userMessage);
    logger.logError(logMessage, {}, error instanceof Error ? error : new Error(String(error)));
};
/**
 * CXone wraps platform failures in a `CXoneSdkError` whose `data` is the raw
 * `HttpResponse`. A 409 means the interaction is not in a state that accepts the
 * operation ("InvalidState") — usually because the tenant auto-accepted or
 * already tore down the contact.
 */
var isConflictError = function (error) {
    var _a;
    return typeof error === 'object' &&
        error !== null &&
        'data' in error &&
        ((_a = error.data) === null || _a === void 0 ? void 0 : _a.status) === 409;
};
var webRtcScriptLoaded = false;
export var loadWebRtcScript = function () {
    return new Promise(function (resolve, reject) {
        // If already loaded, resolve immediately
        if (webRtcScriptLoaded) {
            resolve();
            return;
        }
        // Check if script already exists in DOM
        var existingScript = document.querySelector('script[src*="ac_webrtc"]');
        if (existingScript) {
            webRtcScriptLoaded = true;
            resolve();
            return;
        }
        var script = document.createElement('script');
        script.src = '/ac_webrtc.min.js';
        script.type = 'text/javascript';
        script.onload = function () {
            webRtcScriptLoaded = true;
            resolve();
        };
        script.onerror = function () { return reject(new Error('Failed to load WebRTC script')); };
        document.head.appendChild(script);
    });
};
var NicService = /** @class */ (function () {
    function NicService() {
        this.subscriptions = [];
        this.pendingOutboundCall = false;
        this.ownedContactIds = new Set();
        this.webRtcInitialized = false;
        this.httpUtilService = new HttpUtilService();
    }
    NicService.getTabId = function () {
        var existing = sessionStorage.getItem('tabId');
        if (existing)
            return existing;
        var tabId = "".concat(Math.random().toString(36).substring(7), "-").concat(Date.now());
        sessionStorage.setItem('tabId', tabId);
        return tabId;
    };
    NicService.isWebRtcOwnerTab = function () {
        var tabId = sessionStorage.getItem('tabId');
        return !!tabId && tabId === localStorage.getItem('webRtcOwnerTabId');
    };
    NicService.prototype.claimCallOwnership = function (contactID) {
        localStorage.setItem('activeCallTabId', NicService.getTabId());
        this.broadcastOwnershipChange();
        if (contactID) {
            this.trackContactOwnership(contactID);
        }
    };
    NicService.updateCallInStore = function (updatedCall) {
        var callStore = useCallStore.getState();
        var calls = callStore.calls;
        var existingCallIndex = calls.findIndex(function (c) { var _a; return ((_a = c.call) === null || _a === void 0 ? void 0 : _a.contactID) === updatedCall.contactID; });
        // Handle disconnected calls
        if (updatedCall.status === 'Disconnected') {
            NicService.handleDisconnectedCall(updatedCall, existingCallIndex, calls, callStore);
            return;
        }
        // Handle existing call update
        if (existingCallIndex >= 0) {
            NicService.handleExistingCallUpdate(updatedCall, existingCallIndex, callStore);
            return;
        }
        // Handle new incoming call
        NicService.handleNewCall(updatedCall, calls, callStore);
    };
    NicService.handleDisconnectedCall = function (call, existingIndex, calls, store) {
        var tabId = sessionStorage.getItem('tabId');
        var activeTabId = localStorage.getItem('activeCallTabId');
        // Only the owning tab should save metadata and clean up ownership
        if (tabId === activeTabId) {
            // Save call metadata
            if (call.skill) {
                store.setLastCallSkill(call.skill);
            }
            if (call.contactID) {
                store.setLastCallContactID(call.contactID);
            }
            store.setLastCallRequireDisposition(Boolean(call.requireDisposition));
        }
        // Remove from active calls if it exists (all tabs sync this)
        if (existingIndex >= 0) {
            store.setShowAdditionalDirectory(false);
            var hasOtherActiveCalls = calls.some(function (c, i) { return i !== existingIndex && c.call !== null; });
            if (hasOtherActiveCalls) {
                store.removeCall(existingIndex);
                store.setHasActiveCall(true);
            }
            else {
                store.resetCalls();
                store.setHasActiveCall(false);
            }
        }
    };
    NicService.handleExistingCallUpdate = function (call, index, store) {
        var tabId = sessionStorage.getItem('tabId');
        var activeTabId = localStorage.getItem('activeCallTabId');
        // Save call metadata when call becomes active (before it can disconnect)
        if (call.status === 'Active' && tabId === activeTabId) {
            if (call.skill) {
                store.setLastCallSkill(call.skill);
            }
            if (call.contactID) {
                store.setLastCallContactID(call.contactID);
            }
            store.setLastCallRequireDisposition(Boolean(call.requireDisposition));
        }
        var isOnHold = call.status === 'Holding';
        store.setCalls({
            index: index,
            call: call,
            isOnHold: isOnHold
        });
        store.setHasActiveCall(true);
    };
    NicService.handleNewCall = function (call, calls, store) {
        var emptySlotIndex = calls.findIndex(function (c) { return c.call === null; });
        var targetIndex = emptySlotIndex >= 0 ? emptySlotIndex : calls.length;
        store.setCalls({ index: targetIndex, call: call });
        store.setHasActiveCall(true);
    };
    NicService.getInstance = function () {
        if (!NicService.instance) {
            NicService.instance = new NicService();
            NicService.instance.ownershipChannel = new BroadcastChannel('nic-call-ownership');
        }
        return NicService.instance;
    };
    NicService.prototype.broadcastOwnershipChange = function () {
        this.ownershipChannel.postMessage({ type: 'ownership-changed' });
    };
    NicService.prototype.initEngagement = function () {
        return __awaiter(this, void 0, Promise, function () {
            var _a, isTokenValid, authToken, agentId, tabId, acdSessionId, webRtcOwner, isOwner, joinedExistingSession, error_1;
            var _b;
            return __generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        _c.trys.push([0, 7, , 8]);
                        useNicStore.getState().setLoading(true);
                        console.log('=== STARTING NIC INITIALIZATION ===');
                        _a = CXoneAuth.instance.getAuthState(), isTokenValid = _a.isTokenValid, authToken = _a.authToken;
                        if (!authToken) {
                            throw new Error('Token is missing, attempting to restore...');
                        }
                        if (!isTokenValid) {
                            throw new Error('Token is invalid, attempting to restore...');
                        }
                        console.log('Initializing ACD engagement...');
                        return [4 /*yield*/, CXoneAcdClient.instance.initAcdEngagement()];
                    case 1:
                        _c.sent();
                        agentId = (_b = LocalStorageHelper.getItem(StorageKeys.LAST_LOGGED_IN_AGENT_ID)) === null || _b === void 0 ? void 0 : _b.toString();
                        if (!agentId) {
                            throw new Error('Agent ID not found');
                        }
                        tabId = "".concat(Math.random().toString(36).substring(7), "-").concat(Date.now());
                        sessionStorage.setItem('tabId', tabId);
                        acdSessionId = LocalStorageHelper.getItem(StorageKeys.ACD_SESSION_ID);
                        if (!acdSessionId) {
                            localStorage.removeItem('activeCallTabId');
                            localStorage.removeItem('webRtcOwnerTabId');
                        }
                        webRtcOwner = localStorage.getItem('webRtcOwnerTabId');
                        isOwner = webRtcOwner === tabId;
                        useNicStore.getState().setWebRtcOwner(isOwner);
                        // Set up listener for tab ownership changes
                        this.setupOwnershipListener();
                        // Clear any existing subscriptions before setting up new ones
                        return [4 /*yield*/, this.clearAllSubscriptions()];
                    case 2:
                        // Clear any existing subscriptions before setting up new ones
                        _c.sent();
                        return [4 /*yield*/, this.handleExistingSession()
                            // Only set up subscriptions if we didn't join an existing session
                            // (handleExistingSession sets them up when joining)
                        ];
                    case 3:
                        joinedExistingSession = _c.sent();
                        if (!!joinedExistingSession) return [3 /*break*/, 5];
                        return [4 /*yield*/, this.setupMainSubscriptions()];
                    case 4:
                        _c.sent();
                        _c.label = 5;
                    case 5: return [4 /*yield*/, this.loadAgentSkills(agentId)];
                    case 6:
                        _c.sent();
                        useNicStore.getState().setInitialized(true);
                        console.log('=== NIC INITIALIZATION COMPLETED ===');
                        return [3 /*break*/, 8];
                    case 7:
                        error_1 = _c.sent();
                        useNicStore.getState().setInitialized(false);
                        useNicStore.getState().setLoading(false);
                        showErrorAndLog('Failed to initialize NIC engagement', 'NIC Failed to init engagement:', error_1);
                        return [3 /*break*/, 8];
                    case 8: return [2 /*return*/];
                }
            });
        });
    };
    NicService.prototype.startSession = function () {
        return __awaiter(this, void 0, Promise, function () {
            var coreStore, joined, error_2;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        coreStore = useNicStore.getState();
                        coreStore.setLoading(true);
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 6, 7, 8]);
                        return [4 /*yield*/, this.handleExistingSession()];
                    case 2:
                        joined = _a.sent();
                        if (!!joined) return [3 /*break*/, 5];
                        return [4 /*yield*/, CXoneAcdClient.instance.session.startSession({
                                stationId: '',
                                stationPhoneNumber: 'WebRTC'
                            })];
                    case 3:
                        _a.sent();
                        coreStore.setSessionActive(true);
                        return [4 /*yield*/, this.fetchAndSetAgentState()];
                    case 4:
                        _a.sent();
                        _a.label = 5;
                    case 5: return [3 /*break*/, 8];
                    case 6:
                        error_2 = _a.sent();
                        showErrorAndLog('Failed to start session', 'NIC Failed to start session:', error_2);
                        coreStore.setSessionActive(false);
                        return [3 /*break*/, 8];
                    case 7:
                        coreStore.setLoading(false);
                        return [7 /*endfinally*/];
                    case 8: return [2 /*return*/];
                }
            });
        });
    };
    NicService.prototype.endSession = function () {
        return __awaiter(this, void 0, Promise, function () {
            var isSessionActive, endSessionRequest, error_3;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        isSessionActive = useNicStore.getState().isSessionActive;
                        if (!isSessionActive)
                            return [2 /*return*/];
                        endSessionRequest = {
                            forceLogoff: false,
                            endContacts: true,
                            ignorePersonalQueue: true
                        };
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, CXoneAcdClient.instance.session.endSession(endSessionRequest)];
                    case 2:
                        _a.sent();
                        useNicStore.getState().setSessionActive(false);
                        useCallStore.getState().resetCalls();
                        return [3 /*break*/, 4];
                    case 3:
                        error_3 = _a.sent();
                        showErrorAndLog('Failed to end session', 'NIC Failed to end session:', error_3);
                        return [3 /*break*/, 4];
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    NicService.prototype.changeAgentStatus = function (state, reason) {
        return __awaiter(this, void 0, Promise, function () {
            var error_4;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        if (!CXoneAcdClient.instance.session.setAgentState) {
                            message.error('Failed to change agent status. Please refresh the page.');
                            return [2 /*return*/];
                        }
                        return [4 /*yield*/, CXoneAcdClient.instance.session.setAgentState({
                                state: state,
                                reason: reason || ''
                            })];
                    case 1:
                        _a.sent();
                        return [3 /*break*/, 3];
                    case 2:
                        error_4 = _a.sent();
                        showErrorAndLog('Failed to change agent status', 'NIC Failed to change agent status:', error_4);
                        return [3 /*break*/, 3];
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    // Call management methods
    NicService.prototype.holdResumeCall = function (callIndex) {
        return __awaiter(this, void 0, Promise, function () {
            var currentCall, error_5;
            var _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        currentCall = (_a = useCallStore.getState().calls[callIndex]) === null || _a === void 0 ? void 0 : _a.call;
                        if (!currentCall)
                            return [2 /*return*/];
                        _b.label = 1;
                    case 1:
                        _b.trys.push([1, 6, , 7]);
                        if (!(currentCall.status === 'Active')) return [3 /*break*/, 3];
                        return [4 /*yield*/, currentCall.hold()];
                    case 2:
                        _b.sent();
                        useCallStore.getState().setCalls({ index: callIndex, isOnHold: true });
                        return [3 /*break*/, 5];
                    case 3:
                        if (!(currentCall.status === 'Holding')) return [3 /*break*/, 5];
                        return [4 /*yield*/, currentCall.resume()];
                    case 4:
                        _b.sent();
                        useCallStore.getState().setCalls({ index: callIndex, isOnHold: false });
                        _b.label = 5;
                    case 5: return [3 /*break*/, 7];
                    case 6:
                        error_5 = _b.sent();
                        showErrorAndLog('Failed to hold/resume call', 'NIC Failed to to hold/resume call:', error_5);
                        return [3 /*break*/, 7];
                    case 7: return [2 /*return*/];
                }
            });
        });
    };
    NicService.prototype.muteUnmuteCall = function (callIndex) {
        return __awaiter(this, void 0, Promise, function () {
            var calls, callState, currentCall, isMuted, tabId, activeTabId, localStream, audioTrack;
            var _a;
            return __generator(this, function (_b) {
                calls = useCallStore.getState().calls;
                callState = calls[callIndex];
                currentCall = callState === null || callState === void 0 ? void 0 : callState.call;
                isMuted = callState === null || callState === void 0 ? void 0 : callState.isMuted;
                if (!currentCall)
                    return [2 /*return*/];
                tabId = sessionStorage.getItem('tabId');
                activeTabId = localStorage.getItem('activeCallTabId');
                if (tabId !== activeTabId) {
                    return [2 /*return*/];
                }
                localStream = (callState === null || callState === void 0 ? void 0 : callState.localStream) || ((_a = calls.find(function (c) { return c.localStream; })) === null || _a === void 0 ? void 0 : _a.localStream);
                audioTrack = ((localStream === null || localStream === void 0 ? void 0 : localStream.getAudioTracks()) || [])[0];
                if (!audioTrack) {
                    showErrorAndLog('No audio track available', 'NIC Failed to find audio track for mute/unmute', new Error('audioTrack is missing'));
                    return [2 /*return*/];
                }
                try {
                    if (isMuted) {
                        audioTrack.enabled = true;
                        useCallStore.getState().setCalls({ index: callIndex, isMuted: false });
                    }
                    else {
                        audioTrack.enabled = false;
                        useCallStore.getState().setCalls({ index: callIndex, isMuted: true });
                    }
                }
                catch (error) {
                    showErrorAndLog('Failed to mute/unmute call', 'NIC Failed to mute/unmute:', error);
                }
                return [2 /*return*/];
            });
        });
    };
    NicService.prototype.acceptIncomingCall = function (contactID) {
        return __awaiter(this, void 0, Promise, function () {
            var currentCall, tabId, webRtcOwnerTabId, agentLegId, error_6;
            var _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        currentCall = (_a = useCallStore
                            .getState()
                            .calls.find(function (c) { var _a; return ((_a = c.call) === null || _a === void 0 ? void 0 : _a.contactID) === contactID; })) === null || _a === void 0 ? void 0 : _a.call;
                        if (!currentCall) {
                            showErrorAndLog('Cannot accept call: no call found', "NIC Failed to accept call: no call for contactID ".concat(contactID), new Error('currentCall is missing'));
                            return [2 /*return*/];
                        }
                        // Answering is not instant (leg connect + acceptContact), and a takeover in
                        // front of it makes the wait longer still, so the Accept button spins until
                        // the contact is answered.
                        useNicStore.getState().setAcceptingCall(true);
                        _b.label = 1;
                    case 1:
                        _b.trys.push([1, 8, 9, 10]);
                        tabId = NicService.getTabId();
                        webRtcOwnerTabId = localStorage.getItem('webRtcOwnerTabId');
                        if (webRtcOwnerTabId && webRtcOwnerTabId !== tabId) {
                            showErrorAndLog('Switch to the active call tab to accept this call', 'NIC Failed to accept call: another tab owns the WebRTC connection', new Error("webRtcOwnerTabId ".concat(webRtcOwnerTabId, " does not match tabId ").concat(tabId)));
                            return [2 /*return*/];
                        }
                        if (!!webRtcOwnerTabId) return [3 /*break*/, 3];
                        return [4 /*yield*/, this.takeOverWebRtc({ connectLeg: false })];
                    case 2:
                        _b.sent();
                        if (!NicService.isWebRtcOwnerTab()) {
                            showErrorAndLog('Cannot accept call: this tab is not connected to the phone', 'NIC Failed to accept call: failed to claim WebRTC ownership', new Error('webRtcOwnerTabId was not claimed'));
                            return [2 /*return*/];
                        }
                        _b.label = 3;
                    case 3:
                        agentLegId = this.agentLegId || localStorage.getItem('agentLegId') || undefined;
                        if (!agentLegId) {
                            showErrorAndLog('Cannot accept call: Agent leg ID not available', 'NIC Failed to accept call: agentLegId not available', new Error('agentLegId is missing'));
                            return [2 /*return*/];
                        }
                        NicService.clearRingtoneLock(currentCall.contactID);
                        // This tab owns WebRTC, so it takes over the contact as well.
                        this.claimCallOwnership(currentCall.contactID);
                        CXoneVoiceClient.instance.connectAgentLeg(agentLegId);
                        if (!(currentCall.callType === 'Consult')) return [3 /*break*/, 5];
                        return [4 /*yield*/, CXoneAcdClient.instance.contactManager.contactService.acceptConsultContact(currentCall.contactID)];
                    case 4:
                        _b.sent();
                        return [3 /*break*/, 7];
                    case 5: return [4 /*yield*/, CXoneAcdClient.instance.contactManager.contactService.acceptContact(currentCall.contactID)];
                    case 6:
                        _b.sent();
                        _b.label = 7;
                    case 7: return [3 /*break*/, 10];
                    case 8:
                        error_6 = _b.sent();
                        // Workaround for users that have auto-accept enabled in NIC.
                        if (isConflictError(error_6))
                            return [2 /*return*/];
                        showErrorAndLog('Failed to accept call', 'NIC Failed to accept call:', error_6);
                        return [3 /*break*/, 10];
                    case 9:
                        useNicStore.getState().setAcceptingCall(false);
                        return [7 /*endfinally*/];
                    case 10: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Answers a ringing call from a tab that does not own the WebRTC connection,
     * so the user does not have to hunt for the active tab first. Only the owning
     * tab can answer the agent leg, so ownership has to move here before the
     * accept — `connectLeg` is skipped because `acceptIncomingCall` connects the
     * leg itself once the contact is claimed.
     */
    NicService.prototype.takeOverWebRtcAndAcceptCall = function (contactID) {
        return __awaiter(this, void 0, Promise, function () {
            var _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        useNicStore.getState().setAcceptingCall(true);
                        _b.label = 1;
                    case 1:
                        _b.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, this.takeOverWebRtc({ connectLeg: false })];
                    case 2:
                        _b.sent();
                        return [3 /*break*/, 4];
                    case 3:
                        _a = _b.sent();
                        // takeOverWebRtc already reported the failure to the user
                        useNicStore.getState().setAcceptingCall(false);
                        return [2 /*return*/];
                    case 4: return [4 /*yield*/, this.acceptIncomingCall(contactID)];
                    case 5:
                        _b.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    NicService.clearRingtoneLock = function (contactID) {
        var ringtoneTabId = localStorage.getItem('ringtoneTabId');
        if (ringtoneTabId === null || ringtoneTabId === void 0 ? void 0 : ringtoneTabId.includes(contactID)) {
            localStorage.removeItem('ringtoneTabId');
        }
    };
    NicService.prototype.endCall = function (callIndex) {
        return __awaiter(this, void 0, Promise, function () {
            var call, endButton, error_7;
            var _a, _b;
            return __generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        call = (_a = useCallStore.getState().calls[callIndex]) === null || _a === void 0 ? void 0 : _a.call;
                        if (!call)
                            return [2 /*return*/];
                        _c.label = 1;
                    case 1:
                        _c.trys.push([1, 6, , 7]);
                        NicService.clearRingtoneLock(call.contactID);
                        endButton = (_b = call.callControlButton) === null || _b === void 0 ? void 0 : _b.end;
                        if (!((endButton === null || endButton === void 0 ? void 0 : endButton.isVisible) && (endButton === null || endButton === void 0 ? void 0 : endButton.isEnable))) return [3 /*break*/, 3];
                        return [4 /*yield*/, call.end()];
                    case 2:
                        _c.sent();
                        return [3 /*break*/, 5];
                    case 3: return [4 /*yield*/, CXoneAcdClient.instance.contactManager.contactService.endContact(call.contactID)];
                    case 4:
                        _c.sent();
                        _c.label = 5;
                    case 5:
                        useCallStore.getState().setShowAdditionalDirectory(false);
                        useCallStore.getState().setConferenceActive(false);
                        return [3 /*break*/, 7];
                    case 6:
                        error_7 = _c.sent();
                        showErrorAndLog('Failed to end call', 'NIC Failed to end call:', error_7);
                        return [3 /*break*/, 7];
                    case 7: return [2 /*return*/];
                }
            });
        });
    };
    NicService.prototype.rejectIncomingCall = function (callIndex) {
        return __awaiter(this, void 0, Promise, function () {
            var call, contactService, rejectError_1, error_8;
            var _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        call = (_a = useCallStore.getState().calls[callIndex]) === null || _a === void 0 ? void 0 : _a.call;
                        if (!call)
                            return [2 /*return*/];
                        contactService = CXoneAcdClient.instance.contactManager.contactService;
                        _b.label = 1;
                    case 1:
                        _b.trys.push([1, 7, , 8]);
                        NicService.clearRingtoneLock(call.contactID);
                        _b.label = 2;
                    case 2:
                        _b.trys.push([2, 4, , 6]);
                        return [4 /*yield*/, contactService.rejectContact(call.contactID)];
                    case 3:
                        _b.sent();
                        return [3 /*break*/, 6];
                    case 4:
                        rejectError_1 = _b.sent();
                        if (!isConflictError(rejectError_1))
                            throw rejectError_1;
                        logger.logInfo('NIC reject rejected as InvalidState, ending contact', {
                            contactID: call.contactID,
                            status: call.status
                        });
                        return [4 /*yield*/, contactService.endContact(call.contactID)];
                    case 5:
                        _b.sent();
                        return [3 /*break*/, 6];
                    case 6:
                        useCallStore.getState().setShowAdditionalDirectory(false);
                        useCallStore.getState().setConferenceActive(false);
                        return [3 /*break*/, 8];
                    case 7:
                        error_8 = _b.sent();
                        showErrorAndLog('Failed to reject call', 'NIC Failed to reject call:', error_8);
                        return [3 /*break*/, 8];
                    case 8: return [2 /*return*/];
                }
            });
        });
    };
    NicService.prototype.consultAgent = function (agentId) {
        return __awaiter(this, void 0, Promise, function () {
            var isColdConsult, tabId, response, error_9, error_10;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 5, , 6]);
                        isColdConsult = !useCallStore
                            .getState()
                            .calls.some(function (c) { return c.call !== null; });
                        if (isColdConsult) {
                            this.pendingOutboundCall = true;
                            tabId = sessionStorage.getItem('tabId') ||
                                Math.random().toString(36).substring(7);
                            sessionStorage.setItem('tabId', tabId);
                            localStorage.setItem('activeCallTabId', tabId);
                            this.broadcastOwnershipChange();
                        }
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, CXoneAcdClient.instance.contactManager.voiceService.consultAgent(parseInt(agentId, 10))];
                    case 2:
                        response = _a.sent();
                        console.log('Agent consult initiated:', response);
                        return [3 /*break*/, 4];
                    case 3:
                        error_9 = _a.sent();
                        if (isColdConsult) {
                            this.pendingOutboundCall = false;
                            localStorage.removeItem('activeCallTabId');
                        }
                        throw error_9;
                    case 4: return [3 /*break*/, 6];
                    case 5:
                        error_10 = _a.sent();
                        showErrorAndLog('Failed to consult agent', 'NIC Failed consult agent:', error_10);
                        return [3 /*break*/, 6];
                    case 6: return [2 /*return*/];
                }
            });
        });
    };
    NicService.prototype.holdCurrentAndConsultAgent = function (agentId, callIndex) {
        return __awaiter(this, void 0, Promise, function () {
            var currentCall, response, error_11;
            var _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        currentCall = (_a = useCallStore.getState().calls[callIndex]) === null || _a === void 0 ? void 0 : _a.call;
                        if (!currentCall)
                            return [2 /*return*/];
                        _b.label = 1;
                    case 1:
                        _b.trys.push([1, 5, , 6]);
                        if (!(currentCall.status === 'Active')) return [3 /*break*/, 3];
                        return [4 /*yield*/, currentCall.hold()];
                    case 2:
                        _b.sent();
                        useCallStore.getState().setCalls({ index: callIndex, isOnHold: true });
                        _b.label = 3;
                    case 3: return [4 /*yield*/, CXoneAcdClient.instance.contactManager.voiceService.consultAgent(parseInt(agentId, 10))];
                    case 4:
                        response = _b.sent();
                        console.log('Agent transfer started:', response);
                        return [3 /*break*/, 6];
                    case 5:
                        error_11 = _b.sent();
                        showErrorAndLog('Failed to transfer call', 'NIC Failed to transfer call:', error_11);
                        return [3 /*break*/, 6];
                    case 6: return [2 /*return*/];
                }
            });
        });
    };
    NicService.prototype.holdCurrentAndDialPhone = function (phoneNumber, skillId, callIndex) {
        return __awaiter(this, void 0, Promise, function () {
            var currentCall, response, error_12;
            var _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        currentCall = (_a = useCallStore.getState().calls[callIndex]) === null || _a === void 0 ? void 0 : _a.call;
                        if (!currentCall)
                            return [2 /*return*/];
                        _b.label = 1;
                    case 1:
                        _b.trys.push([1, 5, , 6]);
                        if (!(currentCall.status === 'Active')) return [3 /*break*/, 3];
                        return [4 /*yield*/, currentCall.hold()];
                    case 2:
                        _b.sent();
                        useCallStore.getState().setCalls({ index: callIndex, isOnHold: true });
                        _b.label = 3;
                    case 3: return [4 /*yield*/, CXoneAcdClient.instance.contactManager.voiceService.dialPhone({
                            skillId: skillId,
                            phoneNumber: phoneNumber,
                            parentContactId: currentCall.contactID
                        })];
                    case 4:
                        response = _b.sent();
                        console.log('Phone transfer started:', response);
                        return [3 /*break*/, 6];
                    case 5:
                        error_12 = _b.sent();
                        showErrorAndLog('Failed to transfer call to phone', 'NIC Failed to transfer call to phone:', error_12);
                        return [3 /*break*/, 6];
                    case 6: return [2 /*return*/];
                }
            });
        });
    };
    NicService.prototype.holdCurrentAndDialSkill = function (skillId, callIndex) {
        return __awaiter(this, void 0, Promise, function () {
            var currentCall, response, error_13;
            var _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        currentCall = (_a = useCallStore.getState().calls[callIndex]) === null || _a === void 0 ? void 0 : _a.call;
                        if (!currentCall)
                            return [2 /*return*/];
                        _b.label = 1;
                    case 1:
                        _b.trys.push([1, 5, , 6]);
                        if (!(currentCall.status === 'Active')) return [3 /*break*/, 3];
                        return [4 /*yield*/, currentCall.hold()];
                    case 2:
                        _b.sent();
                        useCallStore.getState().setCalls({ index: callIndex, isOnHold: true });
                        _b.label = 3;
                    case 3: return [4 /*yield*/, CXoneAcdClient.instance.contactManager.voiceService.dialSkill(parseInt(skillId, 10))];
                    case 4:
                        response = _b.sent();
                        console.log('Skill transfer started:', response);
                        return [3 /*break*/, 6];
                    case 5:
                        error_13 = _b.sent();
                        showErrorAndLog('Failed to transfer call to skill', 'NIC Failed to transfer call to skill:', error_13);
                        return [3 /*break*/, 6];
                    case 6: return [2 /*return*/];
                }
            });
        });
    };
    NicService.prototype.transferCall = function () {
        return __awaiter(this, void 0, Promise, function () {
            var response, error_14;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, CXoneAcdClient.instance.contactManager.voiceService.transferContact()];
                    case 1:
                        response = _a.sent();
                        console.log('Agent trasnfer initiated:', response);
                        return [3 /*break*/, 3];
                    case 2:
                        error_14 = _a.sent();
                        showErrorAndLog('Failed to transfer call to agent', 'NIC Failed transfer call to agent:', error_14);
                        return [3 /*break*/, 3];
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Drops the agent out of a conference while leaving the remaining parties
     * talking to each other. `call.end()` (see `endCall`) only tears down the
     * agent's own leg from the SDK's point of view, so the conference collapses to
     * a single participant — `transferContact` hands the contact off instead.
     */
    NicService.prototype.leaveConferenceCall = function (callIndex) {
        return __awaiter(this, void 0, Promise, function () {
            var call, error_15;
            var _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        call = (_a = useCallStore.getState().calls[callIndex]) === null || _a === void 0 ? void 0 : _a.call;
                        if (!call)
                            return [2 /*return*/];
                        _b.label = 1;
                    case 1:
                        _b.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, CXoneAcdClient.instance.contactManager.voiceService.transferContact()];
                    case 2:
                        _b.sent();
                        NicService.clearRingtoneLock(call.contactID);
                        useCallStore.getState().setShowAdditionalDirectory(false);
                        useCallStore.getState().setConferenceInitiated(false);
                        useCallStore.getState().setConferenceActive(false);
                        return [3 /*break*/, 4];
                    case 3:
                        error_15 = _b.sent();
                        showErrorAndLog('Failed to leave conference call', 'NIC Failed to leave conference call:', error_15);
                        return [3 /*break*/, 4];
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    NicService.prototype.startConferenceCall = function () {
        return __awaiter(this, void 0, Promise, function () {
            var response, error_16;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, CXoneAcdClient.instance.contactManager.voiceService.conferenceCall()];
                    case 1:
                        response = _a.sent();
                        useCallStore.getState().setConferenceActive(true);
                        console.log('Conference call initiated:', response);
                        return [3 /*break*/, 3];
                    case 2:
                        error_16 = _a.sent();
                        showErrorAndLog('Failed to initiate conference call', 'NIC Failed to initiate conference call:', error_16);
                        return [3 /*break*/, 3];
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    NicService.prototype.sendDtmfTone = function (digit) {
        return __awaiter(this, void 0, Promise, function () {
            var error_17;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, CXoneAcdClient.instance.contactManager.voiceService.sendDtmf({
                                dtmfSequence: digit,
                                toneDurationMS: 200,
                                toneSpacingMS: 50
                            })];
                    case 1:
                        _a.sent();
                        return [3 /*break*/, 3];
                    case 2:
                        error_17 = _a.sent();
                        showErrorAndLog('Failed to send DTMF tone', 'NIC Failed to send DTMF:', error_17);
                        return [3 /*break*/, 3];
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    NicService.prototype.takeOverWebRtc = function () {
        return __awaiter(this, arguments, Promise, function (_a) {
            var agentLegId, error_18;
            var _b = _a === void 0 ? {} : _a, _c = _b.connectLeg, connectLeg = _c === void 0 ? true : _c;
            return __generator(this, function (_d) {
                switch (_d.label) {
                    case 0:
                        _d.trys.push([0, 2, , 3]);
                        // Clear the old WebRTC owner
                        localStorage.removeItem('webRtcOwnerTabId');
                        // Reset the flag to allow re-initialization (in case this tab was previously an owner)
                        this.webRtcInitialized = false;
                        agentLegId = localStorage.getItem('agentLegId');
                        // Initialize WebRTC in this tab
                        return [4 /*yield*/, this.initWebRTC()
                            // If there's an active call with agentLegId, reconnect to it. Callers that
                            // answer the leg themselves (accepting an incoming call) opt out so the
                            // leg is not connected twice.
                        ];
                    case 1:
                        // Initialize WebRTC in this tab
                        _d.sent();
                        // If there's an active call with agentLegId, reconnect to it. Callers that
                        // answer the leg themselves (accepting an incoming call) opt out so the
                        // leg is not connected twice.
                        if (agentLegId && connectLeg) {
                            CXoneVoiceClient.instance.connectAgentLeg(agentLegId);
                        }
                        // Set this tab as active call owner and tell the other tabs
                        this.claimCallOwnership();
                        return [3 /*break*/, 3];
                    case 2:
                        error_18 = _d.sent();
                        showErrorAndLog('Failed to take over WebRTC', 'NIC Failed to take over WebRTC:', error_18);
                        throw error_18;
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    NicService.prototype.dialOutboundCall = function (dialPhoneRequest) {
        return __awaiter(this, void 0, Promise, function () {
            var existingOwner, response, error_19;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        existingOwner = localStorage.getItem('activeCallTabId');
                        if (existingOwner && existingOwner !== sessionStorage.getItem('tabId')) {
                            return [2 /*return*/];
                        }
                        // Check if we're already dialing from any tab
                        if (this.pendingOutboundCall) {
                            return [2 /*return*/];
                        }
                        // Mark this tab as initiating an outbound call and claim ownership
                        this.pendingOutboundCall = true;
                        this.claimCallOwnership();
                        return [4 /*yield*/, CXoneAcdClient.instance.contactManager.voiceService.dialPhone(dialPhoneRequest)];
                    case 1:
                        response = _a.sent();
                        console.log('Outbound Call initiated:', response);
                        return [3 /*break*/, 3];
                    case 2:
                        error_19 = _a.sent();
                        this.pendingOutboundCall = false;
                        localStorage.removeItem('activeCallTabId');
                        showErrorAndLog('Failed to make outbound call', 'NIC Failed to Outbound Call:', error_19);
                        return [3 /*break*/, 3];
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    NicService.prototype.fetchUnavailableCodes = function () {
        return __awaiter(this, void 0, Promise, function () {
            var result, activeCodes, error_20;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, CXoneAcdClient.instance.getTeamUnavailableCodes()];
                    case 1:
                        result = _a.sent();
                        if (Array.isArray(result)) {
                            activeCodes = result.filter(function (code) { return code.isActive && !code.isAcw; });
                            useNicStore.getState().setUnavailableCodes(activeCodes);
                        }
                        return [3 /*break*/, 3];
                    case 2:
                        error_20 = _a.sent();
                        showErrorAndLog('Failed to fetch unavailable codes', 'NIC Error fetching unavailable codes:', error_20);
                        return [3 /*break*/, 3];
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    NicService.prototype.getWebRtcServiceUrls = function () {
        return __awaiter(this, void 0, Promise, function () {
            var agentSettings, userInfo, e_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 3, , 4]);
                        return [4 /*yield*/, CXoneClient.instance.agentSetting.getAgentSettings()];
                    case 1:
                        agentSettings = (_a.sent());
                        return [4 /*yield*/, CXoneClient.instance.cxoneUser.getUserDetails()];
                    case 2:
                        userInfo = (_a.sent());
                        console.log('=== agentSettings ===:', agentSettings);
                        console.log('=== userInfo ===:', userInfo);
                        if (agentSettings && userInfo.icAgentId) {
                            return [2 /*return*/, {
                                    agentId: userInfo.icAgentId,
                                    agentSettings: agentSettings,
                                    userInfo: userInfo
                                }];
                        }
                        return [3 /*break*/, 4];
                    case 3:
                        e_1 = _a.sent();
                        showErrorAndLog('Failed to get WebRTC service URLs', 'NIC Failed to get WebRTC service URLs:', e_1);
                        return [3 /*break*/, 4];
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    NicService.prototype.initWebRTC = function () {
        return __awaiter(this, void 0, Promise, function () {
            var tabId, webRtcOwnerTabId, settings, appName, audioEl_1, connectionOptions, e_2;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        tabId = sessionStorage.getItem('tabId');
                        webRtcOwnerTabId = localStorage.getItem('webRtcOwnerTabId');
                        // Prevent duplicate initialization - check if this tab already initialized
                        if (this.webRtcInitialized) {
                            return [2 /*return*/];
                        }
                        // Prevent multiple tabs from initializing WebRTC
                        if (webRtcOwnerTabId && webRtcOwnerTabId !== tabId) {
                            return [2 /*return*/];
                        }
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 5, , 6]);
                        return [4 /*yield*/, this.getWebRtcServiceUrls()];
                    case 2:
                        settings = _a.sent();
                        if (!settings)
                            return [2 /*return*/];
                        appName = "CRM Call Manager: ".concat(settings.agentSettings.cxaClientVersion);
                        audioEl_1 = document.getElementById('cxone-audio');
                        if (!audioEl_1)
                            return [2 /*return*/];
                        audioEl_1.autoplay = true;
                        audioEl_1.muted = false;
                        connectionOptions = __assign(__assign({}, settings.agentSettings), { echoCancellation: true, noiseCancellation: true });
                        return [4 /*yield*/, CXoneVoiceClient.instance.connectServer(settings.agentId, connectionOptions, audioEl_1, appName)];
                    case 3:
                        _a.sent();
                        this.webRtcInitialized = true;
                        if (tabId) {
                            localStorage.setItem('webRtcOwnerTabId', tabId);
                            useNicStore.getState().setWebRtcOwner(true);
                        }
                        // workaround for the mute functionality
                        return [4 /*yield*/, CXoneVoiceClient.instance.onCallStatusChanged.subscribe(function (response) {
                                var _a;
                                if (response.status === CXoneVoiceCallStatus.CALL_SHOW_STREAMS) {
                                    var calls = useCallStore.getState().calls;
                                    var targetIndex = calls.findIndex(function (c) { return c.call !== null && !c.localStream; });
                                    var index = targetIndex >= 0 ? targetIndex : 0;
                                    // ensure new stream starts unmuted
                                    var audioTrack = (((_a = response.localStream) === null || _a === void 0 ? void 0 : _a.getAudioTracks()) || [])[0];
                                    if (audioTrack) {
                                        audioTrack.enabled = true;
                                    }
                                    useCallStore.getState().setCalls({
                                        index: index,
                                        localStream: response.localStream,
                                        isMuted: false
                                    });
                                }
                                if (response.status === CXoneVoiceCallStatus.CALL_TERMINATED) {
                                    var calls = useCallStore.getState().calls;
                                    if (!calls.length)
                                        return;
                                    var targetIndex = calls.findIndex(function (c) { return c.localStream !== null; });
                                    if (targetIndex < 0)
                                        return;
                                    useCallStore
                                        .getState()
                                        .setCalls({ index: targetIndex, localStream: null });
                                    // clear audio element to avoid stale srcObject on next call
                                    if (audioEl_1) {
                                        audioEl_1.srcObject = null;
                                    }
                                }
                            })];
                    case 4:
                        // workaround for the mute functionality
                        _a.sent();
                        return [3 /*break*/, 6];
                    case 5:
                        e_2 = _a.sent();
                        showErrorAndLog('Failed to initialize WebRTC', 'NIC Failed to initialize WebRTC:', e_2);
                        return [3 /*break*/, 6];
                    case 6: return [2 /*return*/];
                }
            });
        });
    };
    NicService.prototype.setupMainSubscriptions = function () {
        return __awaiter(this, void 0, Promise, function () {
            var agentStateSub, voiceContactSub, getNextSub, agentLegSub, sessionSub, directorySub;
            var _this = this;
            return __generator(this, function (_a) {
                agentStateSub = CXoneAcdClient.instance.session.agentStateService.agentStateSubject.subscribe(function (agentState) {
                    _this.handleAgentStateChange(agentState);
                    console.log('Agent state changed:', agentState);
                });
                voiceContactSub = CXoneAcdClient.instance.contactManager.voiceContactUpdateEvent.subscribe(function (voiceContact) { return __awaiter(_this, void 0, void 0, function () {
                    var tabId, activeTabId, existingOwner, updatedCall;
                    return __generator(this, function (_a) {
                        console.log('🔔 Voice contact event received:', voiceContact);
                        // Skip update if voiceContact is incomplete or invalid
                        if (!voiceContact || !voiceContact.contactID) {
                            console.log('⚠️ Skipping incomplete voice contact event:', voiceContact);
                            return [2 /*return*/];
                        }
                        tabId = sessionStorage.getItem('tabId');
                        activeTabId = localStorage.getItem('activeCallTabId');
                        // If this tab initiated an outbound call, claim ownership
                        if (this.pendingOutboundCall) {
                            this.trackContactOwnership(voiceContact.contactID);
                            this.pendingOutboundCall = false;
                        }
                        // For incoming calls the WebRTC owner tab claims ownership. Letting any
                        // tab claim it (the first one to see the event, which is a cross-tab
                        // race) leaves the tab that renders the Accept button unable to answer
                        // the agent leg.
                        if (voiceContact.status === 'Ringing' && voiceContact.isInbound) {
                            existingOwner = localStorage.getItem('activeCallTabId');
                            if (!existingOwner && NicService.isWebRtcOwnerTab()) {
                                this.claimCallOwnership(voiceContact.contactID);
                            }
                        }
                        // Clean up ownership tracking when call disconnects (only on owning tab)
                        if (voiceContact.status === 'Disconnected') {
                            if (tabId === activeTabId) {
                                this.cleanupContactOwnership(voiceContact.contactID);
                            }
                        }
                        updatedCall = Object.setPrototypeOf(__assign({}, voiceContact), Object.getPrototypeOf(voiceContact));
                        // All tabs see the call state for UI updates
                        // But only the owning tab manages WebRTC (handled in handleAgentLegEvent)
                        NicService.updateCallInStore(updatedCall);
                        console.log('✅ Voice contact updated:', voiceContact.status, voiceContact.contactID);
                        return [2 /*return*/];
                    });
                }); });
                getNextSub = ACDSessionManager.instance.onGetNextEvent.subscribe(function (events) { return events === null || events === void 0 ? void 0 : events.forEach(function (event) { return NicService.handleChatEvent(event); }); });
                agentLegSub = CXoneAcdClient.instance.session.agentLegEvent.subscribe(function (data) {
                    _this.handleAgentLegEvent(data);
                    console.log('Agent leg event received:', data);
                });
                sessionSub = CXoneAcdClient.instance.session.onAgentSessionChange.subscribe(function (agentSessionChange) {
                    _this.handleSessionChange(agentSessionChange);
                    console.log('Agent session changed:', agentSessionChange);
                });
                directorySub = CXoneClient.instance.directory.directoryEvent.subscribe({
                    next: function (response) {
                        var _a, _b, _c, _d, _e, _f;
                        console.log('📦 Full response:', response);
                        var store = useContactStore.getState();
                        if ((_a = response === null || response === void 0 ? void 0 : response.agentList) === null || _a === void 0 ? void 0 : _a.data) {
                            store.setAgents(__assign(__assign({}, response.agentList), { data: mergeByField('agentId', ((_b = store.agents) === null || _b === void 0 ? void 0 : _b.data) || [], response.agentList.data) }));
                        }
                        if ((_c = response === null || response === void 0 ? void 0 : response.skillList) === null || _c === void 0 ? void 0 : _c.data) {
                            store.setSkills(__assign(__assign({}, response.skillList), { data: mergeByField('skillId', ((_d = store.skills) === null || _d === void 0 ? void 0 : _d.data) || [], response.skillList.data) }));
                        }
                        if ((_e = response === null || response === void 0 ? void 0 : response.teamList) === null || _e === void 0 ? void 0 : _e.data) {
                            store.setTeams(__assign(__assign({}, response.teamList), { data: mergeByField('teamId', ((_f = store.teams) === null || _f === void 0 ? void 0 : _f.data) || [], response.teamList.data) }));
                        }
                        if (response === null || response === void 0 ? void 0 : response.addressBookList) {
                            store.setAddressBookEntries(response.addressBookList);
                        }
                        useContactStore.getState().setContactLoading(false);
                    },
                    error: function (error) {
                        showErrorAndLog('Directory subscription error', 'NIC Directory subscription error:', error);
                        useContactStore.getState().setContactLoading(false);
                    },
                    complete: function () {
                        console.log('📡 Directory subscription completed');
                    }
                });
                this.subscriptions.push(function () { return agentStateSub.unsubscribe(); }, function () { return voiceContactSub.unsubscribe(); }, function () { return getNextSub.unsubscribe(); }, function () { return agentLegSub.unsubscribe(); }, function () { return sessionSub.unsubscribe(); }, function () { return directorySub.unsubscribe(); });
                return [2 /*return*/];
            });
        });
    };
    NicService.prototype.loadDirectoryData = function (directoryRequest) {
        var store = useContactStore.getState();
        store.resetContact();
        store.setContactLoading(true);
        var directory = CXoneClient.instance.directory;
        try {
            directory.getDirectoryData(directoryRequest);
            directory.getDirectoryData(__assign(__assign({}, directoryRequest), { pollingOptions: { isPolling: true, pollingInterval: 10000 } }));
            var cleanup = function () {
                try {
                    directory.terminateDirectoryPolling();
                    console.log('Terminating directory polling:');
                }
                catch (error) {
                    console.log('Error terminating directory polling:', error);
                }
            };
            return cleanup;
        }
        catch (error) {
            showErrorAndLog('Failed to load directory data', 'NIC Failed to load directory data:', error);
            useContactStore.getState().setContactLoading(false);
        }
    };
    /**
     * CleanUp all subscriptions
     */
    NicService.prototype.signOutAndClear = function () {
        return __awaiter(this, void 0, Promise, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.clearAllSubscriptions()];
                    case 1:
                        _a.sent();
                        return [4 /*yield*/, this.endSession()];
                    case 2:
                        _a.sent();
                        Object.values(StorageKeys).forEach(function (key) {
                            localStorage.removeItem(key);
                        });
                        localStorage.removeItem('auth_consumer');
                        localStorage.removeItem('display_mode');
                        localStorage.removeItem('keys');
                        localStorage.removeItem('agentLegId');
                        localStorage.removeItem('ringtoneTabId');
                        localStorage.removeItem('activeCallTabId');
                        localStorage.removeItem('webRtcOwnerTabId');
                        localStorage.removeItem(MESSAGE_SOUND_LOCK_KEY);
                        // Clear persist-and-sync storage
                        localStorage.removeItem('nic-store');
                        localStorage.removeItem('nic-call-store');
                        localStorage.removeItem('nic-contact-store');
                        localStorage.removeItem('nic-chat-store');
                        // Clear owned contacts
                        this.ownedContactIds.clear();
                        // Reset WebRTC flag
                        this.webRtcInitialized = false;
                        // Close BroadcastChannels
                        if (this.ownershipChannel) {
                            this.ownershipChannel.close();
                        }
                        // Reset stores to initial state
                        useNicStore.getState().reset();
                        useCallStore.getState().resetCalls();
                        useContactStore.getState().resetContact();
                        useChatStore.getState().resetChat();
                        return [2 /*return*/];
                }
            });
        });
    };
    NicService.prototype.clearAllSubscriptions = function () {
        return __awaiter(this, void 0, Promise, function () {
            return __generator(this, function (_a) {
                if (!this.subscriptions.length)
                    return [2 /*return*/];
                this.subscriptions.forEach(function (fn) {
                    try {
                        fn();
                    }
                    catch (e) {
                        showErrorAndLog('Failed to clear subscription', 'NIC Failed to clear subscription:', e);
                    }
                });
                this.subscriptions = [];
                return [2 /*return*/];
            });
        });
    };
    NicService.handleChatEvent = function (event) {
        var _a;
        var chatStore = useChatStore.getState();
        var chatContactId = chatStore.chatContactId, isChatAccepted = chatStore.isChatAccepted;
        if (event.Type === TEXT_CONTACT_EVENT && event.ContactID) {
            // Incoming is repeated by every poll until the contact is answered, so a
            // known contact must not reset the chat
            if (event.Status === INCOMING_STATUS &&
                event.ContactID !== chatContactId) {
                chatStore.setIncomingChat(event.ContactID, (_a = event.Skill) !== null && _a !== void 0 ? _a : null);
            }
            if (event.Status === DISCONNECTED_STATUS &&
                event.ContactID === chatContactId) {
                chatStore.resetChat();
            }
            return;
        }
        // Messages carry the room, not the contact, so they belong to the one
        // accepted chat. Agent messages are added once they are sent.
        if (event.Type === TEXT_MESSAGE_EVENT &&
            chatContactId &&
            isChatAccepted &&
            event.PartyType !== AGENT_PARTY_TYPE) {
            chatStore.addMessages([
                {
                    Text: event.Message,
                    TimeStamp: event.TimeStamp,
                    PartyType: event.PartyType,
                    Label: event.Label
                }
            ]);
        }
    };
    NicService.prototype.acceptChat = function (contactId) {
        return __awaiter(this, void 0, Promise, function () {
            var error_21;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, CXoneAcdClient.instance.contactManager.contactService.acceptContact(contactId)];
                    case 1:
                        _a.sent();
                        return [3 /*break*/, 3];
                    case 2:
                        error_21 = _a.sent();
                        // Workaround for users that have auto-accept enabled in NIC.
                        if (!isConflictError(error_21)) {
                            showErrorAndLog('Failed to accept chat', 'NIC Failed to accept chat:', error_21);
                            return [2 /*return*/];
                        }
                        return [3 /*break*/, 3];
                    case 3:
                        useChatStore.getState().setChatAccepted(true);
                        return [4 /*yield*/, this.loadChatTranscript(contactId)];
                    case 4:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    NicService.prototype.rejectChat = function (contactId) {
        return __awaiter(this, void 0, Promise, function () {
            var error_22;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 4]);
                        return [4 /*yield*/, CXoneAcdClient.instance.contactManager.contactService.rejectContact(contactId)];
                    case 1:
                        _a.sent();
                        return [3 /*break*/, 4];
                    case 2:
                        error_22 = _a.sent();
                        if (!isConflictError(error_22)) {
                            showErrorAndLog('Failed to reject chat', 'NIC Failed to reject chat:', error_22);
                            return [2 /*return*/];
                        }
                        // Same as for calls: a contact that can no longer be rejected (e.g.
                        // auto-accepted) is ended instead
                        logger.logInfo('NIC chat reject rejected as InvalidState, ending contact', {
                            contactId: contactId
                        });
                        return [4 /*yield*/, this.endChat(contactId)];
                    case 3:
                        _a.sent();
                        return [2 /*return*/];
                    case 4:
                        useChatStore.getState().resetChat();
                        return [2 /*return*/];
                }
            });
        });
    };
    NicService.prototype.endChat = function (contactId) {
        return __awaiter(this, void 0, Promise, function () {
            var error_23;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, CXoneAcdClient.instance.contactManager.contactService.endContact(contactId)];
                    case 1:
                        _a.sent();
                        return [3 /*break*/, 3];
                    case 2:
                        error_23 = _a.sent();
                        // 409 means the contact is already gone, so the chat is cleared anyway
                        if (!isConflictError(error_23)) {
                            showErrorAndLog('Failed to end chat', 'NIC Failed to end chat:', error_23);
                            return [2 /*return*/];
                        }
                        return [3 /*break*/, 3];
                    case 3:
                        useChatStore.getState().resetChat();
                        return [2 /*return*/];
                }
            });
        });
    };
    NicService.prototype.sendChatMessage = function (contactId, text) {
        return __awaiter(this, void 0, Promise, function () {
            var acdApiBaseUri, url, error_24;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        acdApiBaseUri = ACDSessionManager.instance.cxOneConfig.acdApiBaseUri;
                        url = acdApiBaseUri +
                            SEND_CHAT_TEXT_URI.replace('{sessionId}', ACDSessionManager.instance.getSessionId()).replace('{contactId}', contactId);
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, HttpClient.post(url, __assign(__assign({}, this.getChatRequestInit()), { body: { chatText: text } }))];
                    case 2:
                        _a.sent();
                        useChatStore.getState().addMessages([
                            {
                                Text: text,
                                TimeStamp: new Date().toISOString(),
                                PartyType: AGENT_PARTY_TYPE
                            }
                        ]);
                        return [2 /*return*/, true];
                    case 3:
                        error_24 = _a.sent();
                        showErrorAndLog('Failed to send message', 'NIC Failed to send chat message:', error_24);
                        return [2 /*return*/, false];
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    NicService.prototype.loadChatTranscript = function (contactId) {
        return __awaiter(this, void 0, Promise, function () {
            var acdApiBaseUri, url, response, messages, error_25;
            var _a, _b;
            return __generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        acdApiBaseUri = ACDSessionManager.instance.cxOneConfig.acdApiBaseUri;
                        url = acdApiBaseUri + CHAT_TRANSCRIPT_URI.replace('{contactId}', contactId);
                        _c.label = 1;
                    case 1:
                        _c.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, HttpClient.get(url, this.getChatRequestInit())];
                    case 2:
                        response = _c.sent();
                        messages = (_b = (_a = response.data) === null || _a === void 0 ? void 0 : _a.messages) === null || _b === void 0 ? void 0 : _b.Messages;
                        useChatStore.getState().addMessages(messages);
                        return [3 /*break*/, 4];
                    case 3:
                        error_25 = _c.sent();
                        showErrorAndLog('Failed to load chat transcript', 'NIC Failed to load chat transcript:', error_25);
                        return [3 /*break*/, 4];
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    NicService.prototype.getChatRequestInit = function () {
        return this.httpUtilService.initHeader(ACDSessionManager.instance.accessToken, 'application/json');
    };
    NicService.prototype.handleAgentStateChange = function (agentState) {
        var _a, _b, _c;
        var store = useNicStore.getState();
        var previousState = (_b = (_a = store.agentStatus) === null || _a === void 0 ? void 0 : _a.agentStateData) === null || _b === void 0 ? void 0 : _b.CurrentState;
        var currentState = (_c = agentState === null || agentState === void 0 ? void 0 : agentState.agentStateData) === null || _c === void 0 ? void 0 : _c.CurrentState;
        // A transition into Available means no chat is live any more (e.g. it was
        // ended in MAX and its Disconnected event was missed) — a repeated
        // Available event can arrive in the same batch as it
        if (useChatStore.getState().chatContactId &&
            currentState === AVAILABLE_STATE &&
            previousState !== AVAILABLE_STATE) {
            useChatStore.getState().resetChat();
        }
        store.setAgentStatus(agentState);
    };
    NicService.prototype.handleAgentLegEvent = function (data) {
        var tabId = NicService.getTabId();
        var activeTabId = localStorage.getItem('activeCallTabId');
        if (data.status === 'Dialing') {
            // Store agentLegId for all tabs (for potential takeover)
            this.agentLegId = data.agentLegId;
            localStorage.setItem('agentLegId', data.agentLegId);
            if (this.pendingOutboundCall) {
                // Outbound calls have no Accept step, so connect the leg automatically.
                this.claimCallOwnership();
                // ONLY THIS TAB should process WebRTC events
                CXoneVoiceClient.instance.handleAgentLegEvent(data);
                CXoneVoiceClient.instance.connectAgentLeg(data.agentLegId);
            }
            else if (!activeTabId && NicService.isWebRtcOwnerTab()) {
                this.claimCallOwnership();
            }
            // Secondary tabs: do nothing with the Dialing event
            return;
        }
        // For all other statuses, only the owning tab processes
        var isOwningTab = tabId === activeTabId;
        if (isOwningTab) {
            CXoneVoiceClient.instance.handleAgentLegEvent(data);
        }
        if (data.status === 'Disconnected') {
            this.agentLegId = '';
            this.pendingOutboundCall = false;
            // The agent leg belongs to the session, not to a tab, so every tab has to
            // drop the id once it is gone. Leaving it behind lets a later accept hand
            // an ended leg to connectAgentLeg, which never answers the new contact.
            if (localStorage.getItem('agentLegId') === data.agentLegId) {
                localStorage.removeItem('agentLegId');
            }
            // Only the owning tab releases the contact
            if (isOwningTab) {
                localStorage.removeItem('activeCallTabId');
            }
        }
    };
    NicService.prototype.trackContactOwnership = function (contactId) {
        this.ownedContactIds.add(contactId);
    };
    NicService.prototype.cleanupContactOwnership = function (contactId) {
        this.ownedContactIds.delete(contactId);
    };
    NicService.prototype.setupOwnershipListener = function () {
        var _this = this;
        // Listen for ownership changes to update UI
        this.ownershipChannel.onmessage = function () {
            var tabId = sessionStorage.getItem('tabId');
            var webRtcOwner = localStorage.getItem('webRtcOwnerTabId');
            var isOwner = tabId === webRtcOwner;
            useNicStore.getState().setWebRtcOwner(isOwner);
        };
        // Use BroadcastChannel for ownership transfer notifications
        var ownershipTransferChannel = new BroadcastChannel('nic-ownership-transfer');
        ownershipTransferChannel.onmessage = function (event) { return __awaiter(_this, void 0, void 0, function () {
            var _a, type, payload, oldOwnerTabId_1, agentLegId_1, activeTabId, calls, hasCall, tabId_1;
            var _this = this;
            return __generator(this, function (_b) {
                _a = event.data, type = _a.type, payload = _a.payload;
                // Handle owning tab closed with active call - other tabs race to claim ownership
                if (type === 'request-ownership-takeover') {
                    oldOwnerTabId_1 = payload.oldOwnerTabId;
                    agentLegId_1 = localStorage.getItem('agentLegId');
                    activeTabId = localStorage.getItem('activeCallTabId');
                    calls = useCallStore.getState().calls;
                    hasCall = calls.some(function (c) { return c.call !== null && c.call.status !== 'Disconnected'; });
                    if (agentLegId_1 && hasCall && activeTabId) {
                        tabId_1 = sessionStorage.getItem('tabId');
                        if (tabId_1 && tabId_1 !== oldOwnerTabId_1) {
                            // Small delay to avoid race, then check if someone else claimed it
                            setTimeout(function () { return __awaiter(_this, void 0, void 0, function () {
                                var currentOwner, webRtcOwner, error_26;
                                return __generator(this, function (_a) {
                                    switch (_a.label) {
                                        case 0:
                                            currentOwner = localStorage.getItem('activeCallTabId');
                                            webRtcOwner = localStorage.getItem('webRtcOwnerTabId');
                                            if (!(currentOwner === oldOwnerTabId_1 || webRtcOwner === oldOwnerTabId_1 || !webRtcOwner)) return [3 /*break*/, 4];
                                            localStorage.setItem('activeCallTabId', tabId_1);
                                            this.broadcastOwnershipChange();
                                            this.agentLegId = agentLegId_1;
                                            _a.label = 1;
                                        case 1:
                                            _a.trys.push([1, 3, , 4]);
                                            return [4 /*yield*/, this.initWebRTC()];
                                        case 2:
                                            _a.sent();
                                            CXoneVoiceClient.instance.connectAgentLeg(agentLegId_1);
                                            useNicStore.getState().setWebRtcOwner(true);
                                            return [3 /*break*/, 4];
                                        case 3:
                                            error_26 = _a.sent();
                                            logger.logError('Failed to initialize WebRTC and reconnect agent leg', {}, error_26);
                                            return [3 /*break*/, 4];
                                        case 4: return [2 /*return*/];
                                    }
                                });
                            }); }, 100);
                        }
                    }
                }
                return [2 /*return*/];
            });
        }); };
        // Keep reference to clean up later
        this.subscriptions.push(function () { return ownershipTransferChannel.close(); });
        // Cleanup on tab close
        window.addEventListener('beforeunload', function () {
            var _a;
            var tabId = sessionStorage.getItem('tabId');
            var activeTabId = localStorage.getItem('activeCallTabId');
            var ringtoneTabId = localStorage.getItem('ringtoneTabId');
            var webRtcOwnerTabId = localStorage.getItem('webRtcOwnerTabId');
            // Clear WebRTC ownership if this tab owns it
            if (tabId === webRtcOwnerTabId) {
                localStorage.removeItem('webRtcOwnerTabId');
            }
            // Only clear if this is the active tab AND there are no active calls
            // If there are active calls, leave activeCallTabId so another tab can claim it
            if (tabId === activeTabId) {
                var calls = useCallStore.getState().calls;
                var hasActiveCalls = calls.some(function (c) { return c.call !== null && c.call.status !== 'Disconnected'; });
                if (!hasActiveCalls) {
                    localStorage.removeItem('activeCallTabId');
                    localStorage.removeItem('agentLegId');
                }
                // If there are active calls, broadcast for other tabs to take over
                else {
                    var transferChannel = new BroadcastChannel('nic-ownership-transfer');
                    transferChannel.postMessage({
                        type: 'request-ownership-takeover',
                        payload: { oldOwnerTabId: tabId }
                    });
                    transferChannel.close();
                }
            }
            // Clear ringtone and message sound locks if this tab owns them
            if (ringtoneTabId === null || ringtoneTabId === void 0 ? void 0 : ringtoneTabId.startsWith("".concat(tabId, ":"))) {
                localStorage.removeItem('ringtoneTabId');
            }
            if ((_a = localStorage.getItem(MESSAGE_SOUND_LOCK_KEY)) === null || _a === void 0 ? void 0 : _a.startsWith("".concat(tabId, ":"))) {
                localStorage.removeItem(MESSAGE_SOUND_LOCK_KEY);
            }
        });
    };
    NicService.prototype.handleSessionChange = function (agentSessionChange) {
        return __awaiter(this, void 0, Promise, function () {
            var coreStore, _a, activeCallTabId, tabId, isOwningTab;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        coreStore = useNicStore.getState();
                        _a = agentSessionChange.status;
                        switch (_a) {
                            case AgentSessionStatus.JOIN_SESSION_SUCCESS: return [3 /*break*/, 1];
                            case AgentSessionStatus.SESSION_START: return [3 /*break*/, 1];
                            case AgentSessionStatus.SESSION_END: return [3 /*break*/, 4];
                            case AgentSessionStatus.JOIN_SESSION_FAILURE: return [3 /*break*/, 5];
                        }
                        return [3 /*break*/, 6];
                    case 1:
                        activeCallTabId = localStorage.getItem('activeCallTabId');
                        tabId = sessionStorage.getItem('tabId');
                        isOwningTab = !activeCallTabId || activeCallTabId === tabId;
                        if (!(isOwningTab && !this.webRtcInitialized)) return [3 /*break*/, 3];
                        return [4 /*yield*/, this.initWebRTC()
                            // Claim ownership if no one has it
                        ];
                    case 2:
                        _b.sent();
                        // Claim ownership if no one has it
                        if (!activeCallTabId && tabId) {
                            localStorage.setItem('activeCallTabId', tabId);
                            this.broadcastOwnershipChange();
                        }
                        _b.label = 3;
                    case 3:
                        coreStore.setSessionActive(true);
                        useNicStore.getState().setLoading(false);
                        return [3 /*break*/, 7];
                    case 4:
                        coreStore.setSessionActive(false);
                        useChatStore.getState().resetChat();
                        useCallStore.getState().resetCalls();
                        return [3 /*break*/, 7];
                    case 5:
                        coreStore.setSessionActive(false);
                        useNicStore.getState().setLoading(false);
                        return [3 /*break*/, 7];
                    case 6: return [3 /*break*/, 7];
                    case 7: return [2 /*return*/];
                }
            });
        });
    };
    NicService.prototype.loadAgentSkills = function (agentId) {
        return __awaiter(this, void 0, Promise, function () {
            var skills, error_27;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, CXoneAcdClient.instance.getAgentSkills(agentId)];
                    case 1:
                        skills = _a.sent();
                        useNicStore.getState().setAgentSkills(skills);
                        return [3 /*break*/, 3];
                    case 2:
                        error_27 = _a.sent();
                        showErrorAndLog('Failed to load agent skills', 'NIC Failed to load agent skills:', error_27);
                        return [3 /*break*/, 3];
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    NicService.prototype.fetchAndSetAgentState = function () {
        return __awaiter(this, void 0, Promise, function () {
            var agentState;
            return __generator(this, function (_a) {
                try {
                    agentState = CXoneAcdClient.instance.session.agentStateService.getAgentState();
                    if (agentState) {
                        this.handleAgentStateChange(agentState);
                    }
                }
                catch (error) {
                    showErrorAndLog('Failed to fetch agent state', 'NIC Failed to fetch agent state:', error);
                }
                return [2 /*return*/];
            });
        });
    };
    NicService.prototype.handleExistingSession = function () {
        return __awaiter(this, void 0, Promise, function () {
            var result, storedAgentLegId, activeCallTabId, tabId, isOnlyTab, joinError_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 6, , 7]);
                        return [4 /*yield*/, CXoneAcdClient.instance.session.joinSession()];
                    case 1:
                        result = _a.sent();
                        if ('errorType' in result) {
                            throw new Error("JoinSession failed: ".concat(result.errorType, " - ").concat(result.message));
                        }
                        if (result.status !== 200 && result.status !== 202) {
                            throw new Error("JoinSession HTTP error: ".concat(result.status, " ").concat(result.statusText));
                        }
                        console.log('Successfully joined existing session', result.data);
                        storedAgentLegId = localStorage.getItem('agentLegId');
                        if (storedAgentLegId) {
                            this.agentLegId = storedAgentLegId;
                            logger.logInfo('Restored agentLegId from localStorage', {
                                agentLegId: storedAgentLegId
                            });
                        }
                        activeCallTabId = localStorage.getItem('activeCallTabId');
                        tabId = sessionStorage.getItem('tabId');
                        isOnlyTab = !activeCallTabId;
                        if (!(isOnlyTab && !this.webRtcInitialized)) return [3 /*break*/, 3];
                        // This is the first/only tab joining the session
                        // Initialize WebRTC so we're ready for incoming calls
                        return [4 /*yield*/, this.initWebRTC()
                            // Claim ownership
                        ];
                    case 2:
                        // This is the first/only tab joining the session
                        // Initialize WebRTC so we're ready for incoming calls
                        _a.sent();
                        // Claim ownership
                        if (tabId) {
                            localStorage.setItem('activeCallTabId', tabId);
                            this.broadcastOwnershipChange();
                        }
                        _a.label = 3;
                    case 3: 
                    // Secondary tabs skip WebRTC init to avoid conflicts
                    return [4 /*yield*/, this.fetchAndSetAgentState()
                        // Set up subscriptions for this tab AFTER WebRTC check
                        // This ensures the event emitters are ready to receive events
                    ];
                    case 4:
                        // Secondary tabs skip WebRTC init to avoid conflicts
                        _a.sent();
                        // Set up subscriptions for this tab AFTER WebRTC check
                        // This ensures the event emitters are ready to receive events
                        return [4 /*yield*/, this.setupMainSubscriptions()];
                    case 5:
                        // Set up subscriptions for this tab AFTER WebRTC check
                        // This ensures the event emitters are ready to receive events
                        _a.sent();
                        useNicStore.getState().setInitialized(true);
                        useNicStore.getState().setSessionActive(true);
                        useNicStore.getState().setLoading(false);
                        return [2 /*return*/, true];
                    case 6:
                        joinError_1 = _a.sent();
                        logger.logError('NIC Failed to join existing session', {}, joinError_1);
                        useNicStore.getState().setSessionActive(false);
                        useNicStore.getState().setLoading(false);
                        LocalStorageHelper.removeItem(StorageKeys.ACD_SESSION_ID);
                        return [2 /*return*/, false];
                    case 7: return [2 /*return*/];
                }
            });
        });
    };
    return NicService;
}());
export var nicService = NicService.getInstance();
